import React from "react";
import PropTypes from "prop-types";
import {
  MdEdit,
  MdDelete,
  MdArticle,
  MdPerson,
  MdCalendarToday,
  MdLocalOffer,
  MdCategory,
  MdVisibility,
} from "react-icons/md";

const ActualityCard = ({ 
  actuality, 
  onEdit, 
  onDelete, 
  onView, 
  viewMode = "grid", 
  getPhotoUrl,
  getActualityStatus,
  getStatusColor
}) => {
  const status = getActualityStatus(actuality);
  const statusColorClass = getStatusColor(status);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("fr-FR", { 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "news":
        return "text-blue-600 bg-blue-100";
      case "announcement":
        return "text-yellow-600 bg-yellow-100";
      case "event":
        return "text-purple-600 bg-purple-100";
      case "press":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "news":
        return "Actualités";
      case "announcement":
        return "Annonces";
      case "event":
        return "Événements";
      case "press":
        return "Presse";
      default:
        return type;
    }
  };

  // Fonction pour afficher les tags
  const renderTags = (tags) => {
    if (!tags) return null;
    
    // Si tags est un array, le joindre
    const tagsArray = Array.isArray(tags) ? tags : tags.split(',');
    
    return tagsArray.slice(0, 2).map((tag, index) => (
      <span
        key={index}
        className="inline-block rounded-full bg-[#2ca01c]/10 px-2 py-1 text-xs text-[#2ca01c] mr-1"
      >
        {typeof tag === 'string' ? tag.trim() : tag}
      </span>
    ));
  };

  if (viewMode === "list") {
    return (
      <div className="mb-4 overflow-hidden rounded-lg bg-white shadow-sm transition-all duration-300 hover:shadow-lg">
        <div className="flex flex-col md:flex-row">
          {actuality.image ? (
            <div className="h-48 w-full md:h-auto md:w-64">
              <img
                src={actuality.image}
                alt={actuality.title}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="flex h-48 w-full items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10 md:h-auto md:w-64">
              <MdArticle className="text-6xl text-[#2ca01c]/40" />
            </div>
          )}

          <div className="flex flex-1 flex-col p-5">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
                >
                  {status}
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${getTypeColor(actuality.type)}`}>
                  {getTypeLabel(actuality.type)}
                </span>
              </div>
            </div>

            <h2 className="mb-2 text-xl font-semibold text-[#333333] line-clamp-2">
              {actuality.title}
            </h2>

            <p className="mb-4 line-clamp-2 text-gray-600">
              {actuality.description || "Aucune description fournie"}
            </p>

            <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg bg-[#f9f9f9] p-3 sm:grid-cols-2">
              <p className="flex items-center text-gray-700 text-sm">
                <MdPerson className="mr-2 text-[#7028a9]" />
                <span className="font-medium">Auteur:</span>
                <span className="ml-2">{actuality.author}</span>
              </p>
              <p className="flex items-center text-gray-700 text-sm">
                <MdCalendarToday className="mr-2 text-[#7028a9]" />
                <span className="font-medium">Date:</span>
                <span className="ml-2">{formatDate(actuality.date)}</span>
              </p>
              <p className="flex items-center text-gray-700 text-sm">
                <MdCategory className="mr-2 text-[#7028a9]" />
                <span className="font-medium">Catégorie:</span>
                <span className="ml-2">{actuality.category}</span>
              </p>
              {actuality.tags && (
                <div className="flex items-center text-gray-700 text-sm">
                  <MdLocalOffer className="mr-2 text-[#7028a9]" />
                  <span className="font-medium">Tags:</span>
                  <div className="ml-2 flex flex-wrap">
                    {renderTags(actuality.tags)}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-auto flex space-x-3">
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
                onClick={() => onView(actuality)}
              >
                <MdVisibility className="mr-1" /> Lire
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
                onClick={() => onEdit(actuality)}
              >
                <MdEdit className="mr-1" /> Modifier
              </button>
              <button
                className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
                onClick={() => onDelete(actuality.id)}
              >
                <MdDelete className="mr-1" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm transition-all duration-300 hover:shadow-xl">
      {actuality.image ? (
        <div className="relative h-48 overflow-hidden">
          <img
            src={actuality.image}
            alt={actuality.title}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className="hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10"
          >
            <MdArticle className="text-6xl text-[#2ca01c]/40" />
          </div>
          <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/60 to-transparent p-3">
            <div className="flex items-center justify-between">
              <span
                className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
              >
                {status}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-medium bg-white/90 ${getTypeColor(actuality.type).replace('bg-', 'text-')}`}>
                {getTypeLabel(actuality.type)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative flex h-48 items-center justify-center bg-gradient-to-br from-[#2ca01c]/10 to-[#7028a9]/10">
          <MdArticle className="text-6xl text-[#2ca01c]/40" />
          <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/60 to-transparent p-3">
            <div className="flex items-center justify-between">
              <span
                className={`rounded-full ${statusColorClass} px-3 py-1 text-xs font-medium text-white`}
              >
                {status}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-medium bg-white/90 ${getTypeColor(actuality.type).replace('bg-', 'text-')}`}>
                {getTypeLabel(actuality.type)}
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-5">
        <h2 className="mb-3 border-b border-gray-100 pb-2 text-xl font-semibold text-[#333333] line-clamp-2">
          {actuality.title}
        </h2>
        <p className="mb-4 line-clamp-3 text-gray-600">
          {actuality.description || "Aucune description fournie"}
        </p>
        
        <div className="mb-6 space-y-2 rounded-lg bg-[#f9f9f9] p-3">
          <p className="flex items-center text-gray-700 text-sm">
            <MdPerson className="mr-2 text-[#7028a9]" />
            <span className="font-medium">Auteur:</span>
            <span className="ml-2">{actuality.author}</span>
          </p>
          <p className="flex items-center text-gray-700 text-sm">
            <MdCalendarToday className="mr-2 text-[#7028a9]" />
            <span className="font-medium">Date:</span>
            <span className="ml-2">{formatDate(actuality.date)}</span>
          </p>
          <p className="flex items-center text-gray-700 text-sm">
            <MdCategory className="mr-2 text-[#7028a9]" />
            <span className="font-medium">Catégorie:</span>
            <span className="ml-2">{actuality.category}</span>
          </p>
          {actuality.tags && (
            <div className="flex items-start text-gray-700 text-sm">
              <MdLocalOffer className="mr-2 text-[#7028a9] mt-1" />
              <div>
                <span className="font-medium">Tags:</span>
                <div className="ml-2 flex flex-wrap mt-1">
                  {renderTags(actuality.tags)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex flex-col space-y-2">
          <button
            className="flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50"
            onClick={() => onView(actuality)}
          >
            <MdVisibility className="mr-1" /> Lire l'article
          </button>
          <div className="flex space-x-3">
            <button
              className="flex flex-1 items-center justify-center rounded-md border border-[#2ca01c] bg-white px-3 py-2 text-sm font-medium text-[#2ca01c] transition-colors duration-200 hover:bg-[#2ca01c] hover:text-white"
              onClick={() => onEdit(actuality)}
            >
              <MdEdit className="mr-1" /> Modifier
            </button>
            <button
              className="flex flex-1 items-center justify-center rounded-md border border-[#e21b1b] bg-white px-3 py-2 text-sm font-medium text-[#e21b1b] transition-colors duration-200 hover:bg-[#e21b1b] hover:text-white"
              onClick={() => onDelete(actuality.id)}
            >
              <MdDelete className="mr-1" /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
ActualityCard.propTypes = {
  actuality: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    author: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    category: PropTypes.string,
    tags: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
    ]),
    type: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  viewMode: PropTypes.string,
  getPhotoUrl: PropTypes.func,
  getActualityStatus: PropTypes.func.isRequired,
  getStatusColor: PropTypes.func.isRequired,
};

export default ActualityCard;